import { motion } from "motion/react"

// Animated Agent Icon - Single person with pulsing aura
export function AgentIcon({ isActive = false }: { isActive?: boolean }) {
  return (
    <motion.svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      initial={{ scale: 0.9 }}
      animate={{ scale: isActive ? 1.1 : 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      {/* Pulsing background aura */}
      <motion.circle
        cx="20"
        cy="20"
        r="18"
        fill="url(#agentGradient)"
        initial={{ opacity: 0.3, scale: 0.8 }}
        animate={{
          opacity: isActive ? [0.4, 0.6, 0.4] : 0.3,
          scale: isActive ? [1, 1.1, 1] : 1,
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Head */}
      <motion.circle
        cx="20"
        cy="14"
        r="6"
        fill={isActive ? "#F37020" : "#888"}
        initial={{ y: 0 }}
        animate={{ y: isActive ? [0, -1, 0] : 0 }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Body */}
      <motion.path
        d="M10 32C10 26.477 14.477 22 20 22C25.523 22 30 26.477 30 32V34H10V32Z"
        fill={isActive ? "#F37020" : "#888"}
        initial={{ y: 0 }}
        animate={{ y: isActive ? [0, -1, 0] : 0 }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
      />

      <defs>
        <radialGradient id="agentGradient" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#F37020" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#F37020" stopOpacity="0" />
        </radialGradient>
      </defs>
    </motion.svg>
  )
}

// Animated Solo Agent Icon - Person with orbiting stars
export function SoloAgentIcon({ isActive = false }: { isActive?: boolean }) {
  return (
    <motion.svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      initial={{ scale: 0.9 }}
      animate={{ scale: isActive ? 1.1 : 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      {/* Orbiting ring */}
      <motion.ellipse
        cx="20"
        cy="20"
        rx="17"
        ry="6"
        stroke={isActive ? "#8B5CF6" : "#555"}
        strokeWidth="1"
        strokeDasharray="4 4"
        fill="none"
        initial={{ rotate: 0, opacity: 0.3 }}
        animate={{
          rotate: isActive ? 360 : 0,
          opacity: isActive ? 0.8 : 0.3,
        }}
        transition={{
          rotate: { duration: 8, repeat: Infinity, ease: "linear" },
          opacity: { duration: 0.3 },
        }}
        style={{ transformOrigin: "center" }}
      />

      {/* Orbiting star 1 */}
      <motion.circle
        cx="37"
        cy="20"
        r="2"
        fill="#8B5CF6"
        initial={{ opacity: 0 }}
        animate={{
          opacity: isActive ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
      />

      {/* Head */}
      <motion.circle
        cx="20"
        cy="14"
        r="5"
        fill={isActive ? "#8B5CF6" : "#888"}
      />

      {/* Body */}
      <motion.path
        d="M12 32C12 27.029 16.029 23 21 23H19C23.971 23 28 27.029 28 32V34H12V32Z"
        fill={isActive ? "#8B5CF6" : "#888"}
      />

      {/* Star badge */}
      <motion.path
        d="M30 10L31.5 13L35 13.5L32.5 16L33 19.5L30 18L27 19.5L27.5 16L25 13.5L28.5 13L30 10Z"
        fill={isActive ? "#FBBF24" : "#666"}
        initial={{ scale: 0.8, rotate: 0 }}
        animate={{
          scale: isActive ? [1, 1.2, 1] : 0.8,
          rotate: isActive ? [0, 10, -10, 0] : 0,
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{ transformOrigin: "30px 14px" }}
      />
    </motion.svg>
  )
}

// Animated Agency Icon - Building with data streams
export function AgencyIcon({ isActive = false }: { isActive?: boolean }) {
  return (
    <motion.svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      initial={{ scale: 0.9 }}
      animate={{ scale: isActive ? 1.1 : 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      {/* Data streams */}
      {[0, 1, 2].map((i) => (
        <motion.rect
          key={i}
          x={8 + i * 10}
          y={4}
          width="2"
          height="6"
          rx="1"
          fill={isActive ? "#FBBF24" : "#555"}
          initial={{ scaleY: 0.3, opacity: 0.3 }}
          animate={{
            scaleY: isActive ? [0.3, 1, 0.3] : 0.3,
            opacity: isActive ? [0.3, 1, 0.3] : 0.3,
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.2,
          }}
          style={{ transformOrigin: `${9 + i * 10}px 7px` }}
        />
      ))}

      {/* Building base */}
      <motion.rect
        x="6"
        y="12"
        width="28"
        height="24"
        rx="2"
        fill={isActive ? "#FBBF24" : "#888"}
      />

      {/* Windows row 1 */}
      {[0, 1, 2].map((i) => (
        <motion.rect
          key={`w1-${i}`}
          x={10 + i * 8}
          y="16"
          width="4"
          height="4"
          rx="0.5"
          fill={isActive ? "#1a1a1a" : "#555"}
          initial={{ opacity: 0.5 }}
          animate={{
            opacity: isActive ? [0.5, 1, 0.5] : 0.5,
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.3,
          }}
        />
      ))}

      {/* Windows row 2 */}
      {[0, 1, 2].map((i) => (
        <motion.rect
          key={`w2-${i}`}
          x={10 + i * 8}
          y="24"
          width="4"
          height="4"
          rx="0.5"
          fill={isActive ? "#1a1a1a" : "#555"}
          initial={{ opacity: 0.5 }}
          animate={{
            opacity: isActive ? [0.5, 1, 0.5] : 0.5,
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: (2 - i) * 0.3,
          }}
        />
      ))}

      {/* Door */}
      <motion.rect
        x="16"
        y="30"
        width="8"
        height="6"
        rx="1"
        fill={isActive ? "#1a1a1a" : "#555"}
      />
    </motion.svg>
  )
}

// Animated checkmark for success states
export function AnimatedCheckmark({ className = "" }: { className?: string }) {
  return (
    <motion.svg
      width="80"
      height="80"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Outer ring */}
      <motion.circle
        cx="40"
        cy="40"
        r="36"
        stroke="#F37020"
        strokeWidth="3"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />

      {/* Inner glow */}
      <motion.circle
        cx="40"
        cy="40"
        r="30"
        fill="url(#checkGlow)"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
      />

      {/* Checkmark */}
      <motion.path
        d="M25 42L35 52L55 28"
        stroke="#F37020"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.5, delay: 0.6, ease: "easeOut" }}
      />

      {/* Sparkles */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const angle = (i * 60 * Math.PI) / 180
        const x = 40 + Math.cos(angle) * 45
        const y = 40 + Math.sin(angle) * 45
        return (
          <motion.circle
            key={i}
            cx={x}
            cy={y}
            r="3"
            fill="#FBBF24"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.5, 0], opacity: [0, 1, 0] }}
            transition={{
              duration: 0.6,
              delay: 0.8 + i * 0.1,
              ease: "easeOut",
            }}
          />
        )
      })}

      <defs>
        <radialGradient id="checkGlow" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#F37020" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#F37020" stopOpacity="0" />
        </radialGradient>
      </defs>
    </motion.svg>
  )
}

// Loading spinner with Oval branding
export function OvalSpinner({ size = 40 }: { size?: number }) {
  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <motion.circle
        cx="20"
        cy="20"
        r="16"
        stroke="#2a2a2a"
        strokeWidth="3"
        fill="none"
      />
      <motion.circle
        cx="20"
        cy="20"
        r="16"
        stroke="url(#spinnerGradient)"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
        strokeDasharray="80 100"
        initial={{ rotate: 0 }}
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        style={{ transformOrigin: "center" }}
      />
      <defs>
        <linearGradient id="spinnerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F37020" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
      </defs>
    </motion.svg>
  )
}
